// Module 09 §6 — the feature's ONLY HTTP boundary (contract 03 §15/§80). Nothing else in
// `features/dashboards` imports `appHttp`; every method returns an unwrapped DTO and every
// request is feature-owned (`suppressGlobalErrorHandler`), so a 409/403/400 rejects as
// `ApiError` for the save funnel to branch on instead of raising a global toast.
//
// TWO BODIES DIVERGE FROM CONTRACT §6, deliberately — the contract's table is a TARGET shape
// whose backend halves are still open (§18: "BE-09-03 must ratify the account-center write
// body"; the issue names BE-09-02 for delete). Where the target and the DEPLOYED backend
// disagree, this class sends what the deployed backend accepts today, because a v4 client that
// cannot delete is worse than one that matches an unratified doc:
//
//   • delete → `{ dashboard_id }`, NOT `{ ids }`. Four live v3 call sites send the singular key
//     (`CustomDashboardDeleteModal.vue:41`, `AccountCenterDeleteModal.vue:41`,
//     `CustomDashboardStarterStep.vue:657`, `PackageLimitModal.vue:196`) and none sends `ids`.
//   • account-center upsert → the existing-record key is `account_center_id`, NOT `id`
//     (`AccountCenter.vue:108`).
//
// Both are re-checked the moment BE-09-02/BE-09-03 land; see the PR body.
import type { AxiosInstance, AxiosRequestConfig } from 'axios'

import type {
  DashboardDefinitionDto,
  DashboardDefinitionListDto,
  DashboardTemplate,
  UpsertBody,
  UpsertResult,
} from '../models/api.types'

import { appHttp, unwrap, unwrapEnvelope, type ApiEnvelope } from '@/shared/http'

/**
 * Errors route to the caller, not the global toast (both flag forms, sibling parity with
 * `ScorecardApi`). `SaveDashboardFlow` owns the 409/403/400 UX; a raw toast would fire
 * *alongside* it.
 */
const FEATURE_OWNED_ERRORS: AxiosRequestConfig = {
  suppressGlobalErrorHandler: true,
  meta: { suppressGlobalErrorHandler: true },
}

/** The account-center upsert body: same as `UpsertBody` but keyed `account_center_id` (v3 parity). */
export interface AccountCenterUpsertBody extends Omit<UpsertBody, 'id'> {
  account_center_id?: number
}

export class DashboardApi {
  constructor(private readonly http: AxiosInstance = appHttp) {}

  /* ── Reads ──────────────────────────────────────────────────────────────── */

  /**
   * `GET custom-dashboard/definitions/{wsId}/` — one workspace's dashboards.
   *
   * `unwrap`, not `unwrapEnvelope`: `workspace_attributes` / `workspace_integrations` are
   * SIBLINGS of `data` inside the envelope, not nested under it (v3 reads
   * `d?.data?.workspace_attributes` next to `res?.data?.data`, `dashboardStore.ts:114-118`), so
   * unwrapping to `.data` would return the array and drop them. Contract §6's snippet pairs the
   * full-body return type with `unwrapEnvelope`, which cannot produce it — corrected here.
   */
  async listDefinitions(wsId: number | string): Promise<DashboardDefinitionListDto> {
    const response = await this.http.get<DashboardDefinitionListDto>(
      `custom-dashboard/definitions/${wsId}/`,
      FEATURE_OWNED_ERRORS,
    )
    return unwrap(response)
  }

  /** `GET custom-dashboard/definitions/account-center/{accountId}/` — account-center dashboards. */
  async listAccountCenters(accountId: number | string): Promise<DashboardDefinitionDto[]> {
    const response = await this.http.get<ApiEnvelope<DashboardDefinitionDto[]>>(
      `custom-dashboard/definitions/account-center/${accountId}/`,
      FEATURE_OWNED_ERRORS,
    )
    return unwrapEnvelope(response) ?? []
  }

  /** `GET custom-dashboard/template/all` — starter templates. Template WRITE CRUD is module 19's. */
  async listTemplates(): Promise<DashboardTemplate[]> {
    const response = await this.http.get<ApiEnvelope<DashboardTemplate[]>>(
      'custom-dashboard/template/all',
      FEATURE_OWNED_ERRORS,
    )
    return unwrapEnvelope(response) ?? []
  }

  /* ── Writes ─────────────────────────────────────────────────────────────── */

  /**
   * `POST custom-dashboard/definitions/{wsId}/upsert/` — create or update.
   *
   * Rejects `ApiError{kind:'conflict'}` on a stale `version` (409) and
   * `ApiError{kind:'validation'}` when the backend's `sections` validator refuses the payload
   * (400, body = `SectionsValidationError[]`). `SaveDashboardFlow` reconciles both; neither is
   * this class's business.
   */
  async upsert(wsId: number | string, body: UpsertBody): Promise<UpsertResult> {
    const response = await this.http.post<ApiEnvelope<UpsertResult>>(
      `custom-dashboard/definitions/${wsId}/upsert/`,
      body,
      FEATURE_OWNED_ERRORS,
    )
    return unwrapEnvelope(response)
  }

  /** `POST custom-dashboard/definitions/{wsId}/delete/` — body `{ dashboard_id }` (see header). */
  async remove(wsId: number | string, dashboardId: number): Promise<void> {
    await this.http.post(
      `custom-dashboard/definitions/${wsId}/delete/`,
      { dashboard_id: dashboardId },
      FEATURE_OWNED_ERRORS,
    )
  }

  /** `POST custom-dashboard/definitions/account-center/{accountId}/upsert/`. */
  async upsertAccountCenter(
    accountId: number | string,
    body: AccountCenterUpsertBody,
  ): Promise<UpsertResult> {
    const response = await this.http.post<ApiEnvelope<UpsertResult>>(
      `custom-dashboard/definitions/account-center/${accountId}/upsert/`,
      body,
      FEATURE_OWNED_ERRORS,
    )
    return unwrapEnvelope(response)
  }

  /** `POST custom-dashboard/definitions/account-center/{accountId}/delete/`. */
  async removeAccountCenter(accountId: number | string, dashboardId: number): Promise<void> {
    await this.http.post(
      `custom-dashboard/definitions/account-center/${accountId}/delete/`,
      { dashboard_id: dashboardId },
      FEATURE_OWNED_ERRORS,
    )
  }

  // NO `validateChartConfig` HERE, and no chart-list read either.
  //
  // Contract §6 row 5 assigns `POST custom-dashboard/chart-types/validate/` to this class, but
  // that predates module 08 shipping `features/reports/api/ChartValidationApi.ts`, which already
  // owns that exact endpoint AND validates the response body through
  // `parseChartValidationResult` (this class has no such parser). Adding a second poster of one
  // endpoint is the same duplication ledger A7 removes on the read side, so 09 consumes 08's when
  // the widget builder lands (`useWidgetBuilder`, a later ticket) rather than growing its own.
  // Nothing in this headless slice calls it. Contract §6 needs the correction — see the PR body.
  //
  // The chart-list GET stays absent for the original reason: runtime availability, labels, limits
  // and field roles come from 08's `useVisualizationTypeCatalog()` (ledger A7).
}

export const dashboardApi = new DashboardApi()
